import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Alert, AlertDescription } from '../components/ui/alert';
import { ConfirmDialog } from '../components/ui/confirm-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  uploadConnectorService,
  UploadProduct,
  UploadConnector,
  UploadedImage,
} from '../services/uploadConnector';
import { organizationService } from '../services/organizations';
import { useOrganization } from '../contexts/OrganizationContext';
import { amazonExportService } from '../services/amazonExport';
import { ImageViewerModal } from '../components/assets/ImageViewerModal';
import { ImageCompareModal } from '../components/assets/ImageCompareModal';
import { useToast } from '../hooks/use-toast';
import {
  ArrowLeft,
  Save,
  Loader2,
  Trash2,
  Calendar,
  Package,
  Image as ImageIcon,
  X,
  FileImage,
  Check,
  Barcode,
  FileText,
  CheckCircle2,
  Clock,
  Palette,
  FileEdit,
  AlertCircle,
  Wand2,
  ScanBarcode,
  Undo2,
  SplitSquareHorizontal,
  Coins,
  Send,
  Settings,
  DollarSign,
  Tag,
  ShoppingCart,
  Search,
} from 'lucide-react';

export const UploadProductDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { currentOrganization } = useOrganization();
  const amazonEnabled = !!currentOrganization?.settings?.amazon_enabled;

  const [connector, setConnector] = useState<UploadConnector | null>(null);
  const [product, setProduct] = useState<UploadProduct | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Credit rate fetched from backend
  const [studioRetouchCreditsPerImage, setStudioRetouchCreditsPerImage] = useState<number | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    name_en: '',
    name_ar: '',
    description_en: '',
    description_ar: '',
    barcode: '',
    sku: '',
    price: '',
    quantity: '',
    product_type: '',
    material: '',
    brand: '',
    size: '',
    color: '',
    status: 'draft' as 'draft' | 'partial' | 'complete',
    retouch_instructions: '',
  });

  // Approval state
  const [isApprovingMedia, setIsApprovingMedia] = useState(false);
  const [isApprovingContent, setIsApprovingContent] = useState(false);

  // Delete state
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  // Image removal state
  const [imageToRemove, setImageToRemove] = useState<UploadedImage | null>(null);
  const [isRemovingImage, setIsRemovingImage] = useState(false);

  // Fullscreen viewer state - stores the full image object for comparison support
  const [fullscreenImage, setFullscreenImage] = useState<UploadedImage | null>(null);

  // Compare modal state
  const [comparingImage, setComparingImage] = useState<UploadedImage | null>(null);

  // Studio Retouch state
  const [isRetouching, setIsRetouching] = useState(false);
  const [isUpdatingImageType, setIsUpdatingImageType] = useState<string | null>(null);
  const [isRevertingImage, setIsRevertingImage] = useState<string | null>(null);
  const [isRetouchingSingleImage, setIsRetouchingSingleImage] = useState<string | null>(null);
  const [showNoBarcodeWarning, setShowNoBarcodeWarning] = useState(false);
  const [isSavingInstructions, setIsSavingInstructions] = useState(false);
  const [instructionsSaved, setInstructionsSaved] = useState(true); // Track if instructions match saved value

  // Single image retouch dialog state
  const [singleRetouchImage, setSingleRetouchImage] = useState<UploadedImage | null>(null);
  const [singleRetouchInstructions, setSingleRetouchInstructions] = useState('');

  // RetailOS push state
  const [retailosConfigured, setRetailosConfigured] = useState(false);
  const [isPushingToRetailos, setIsPushingToRetailos] = useState(false);

  // Amazon export state
  const [amazonEnrichmentExists, setAmazonEnrichmentExists] = useState(false);
  const [amazonLastExported, setAmazonLastExported] = useState<string | null>(null);

  // Polling interval ref for cleanup
  const pollingIntervalRef = React.useRef<NodeJS.Timeout | null>(null);

  // Fetch connector, credit rates, and RetailOS config
  useEffect(() => {
    const fetchConnectorAndRates = async () => {
      try {
        const [conn, ratesResponse, retailosSettings] = await Promise.all([
          uploadConnectorService.getDefaultConnector(),
          uploadConnectorService.getCreditRates(),
          organizationService.getRetailOSSettings().catch(() => ({ retailos_configured: false }))
        ]);
        setConnector(conn);
        // Get gemini_studio_retouch rate from the rates
        if (ratesResponse.rates?.gemini_studio_retouch) {
          setStudioRetouchCreditsPerImage(ratesResponse.rates.gemini_studio_retouch);
        }
        // Set RetailOS configuration status
        setRetailosConfigured(retailosSettings.retailos_configured);
      } catch (err) {
        console.error('Failed to fetch connector or credit rates:', err);
      }
    };
    fetchConnectorAndRates();
  }, []);

  // Fetch product
  const fetchProduct = useCallback(async () => {
    if (!connector || !id) return;

    setIsLoading(true);
    try {
      const prod = await uploadConnectorService.getProduct(connector.id, id);
      setProduct(prod);
      setFormData({
        name_en: prod.name_en || '',
        name_ar: prod.name_ar || '',
        description_en: prod.description_en || '',
        description_ar: prod.description_ar || '',
        barcode: prod.barcode || '',
        sku: prod.sku || '',
        price: prod.price != null ? prod.price.toString() : '',
        quantity: prod.quantity != null ? prod.quantity.toString() : '',
        product_type: prod.product_type || '',
        material: prod.material || '',
        brand: prod.brand || '',
        size: prod.size || '',
        color: prod.color || '',
        status: prod.status,
        retouch_instructions: prod.retouch_instructions || '',
      });

      // Set first image as selected (prefer processed_url if available)
      if (prod.uploaded_images && prod.uploaded_images.length > 0) {
        const firstImg = prod.uploaded_images[0];
        setSelectedImage(firstImg.processed_url || firstImg.thumbnail_url || firstImg.original_url);
      }

      // Check for existing Amazon enrichment
      if (prod.media_approved && prod.content_approved) {
        amazonExportService.getEnrichment(prod.id).then((enrichment) => {
          setAmazonEnrichmentExists(true);
          setAmazonLastExported(enrichment.last_exported_at);
        }).catch(() => {
          setAmazonEnrichmentExists(false);
          setAmazonLastExported(null);
        });
      }
    } catch (err) {
      console.error('Failed to fetch product:', err);
      toast({
        title: 'Error',
        description: 'Failed to load product',
        variant: 'destructive',
      });
      navigate('/upload-products');
    } finally {
      setIsLoading(false);
    }
  }, [connector, id, navigate, toast]);

  useEffect(() => {
    fetchProduct();
  }, [fetchProduct]);

  // Restore processing state from product and manage polling
  useEffect(() => {
    if (!product || !connector) return;

    const isProcessing = product.processing_status === 'processing';

    // Restore processing state if the product is being processed
    if (isProcessing && !isRetouching) {
      setIsRetouching(true);
    }

    // Start polling if processing
    if (isProcessing) {
      // Clear any existing interval
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }

      // Poll every 3 seconds
      pollingIntervalRef.current = setInterval(async () => {
        try {
          const updatedProduct = await uploadConnectorService.getProduct(connector.id, product.id);
          if (updatedProduct.processing_status !== 'processing') {
            // Processing completed, stop polling and update
            if (pollingIntervalRef.current) {
              clearInterval(pollingIntervalRef.current);
              pollingIntervalRef.current = null;
            }
            setIsRetouching(false);
            setProduct(updatedProduct);
            setFormData({
              name_en: updatedProduct.name_en || '',
              name_ar: updatedProduct.name_ar || '',
              description_en: updatedProduct.description_en || '',
              description_ar: updatedProduct.description_ar || '',
              barcode: updatedProduct.barcode || '',
              sku: updatedProduct.sku || '',
              price: updatedProduct.price != null ? updatedProduct.price.toString() : '',
              quantity: updatedProduct.quantity != null ? updatedProduct.quantity.toString() : '',
              product_type: updatedProduct.product_type || '',
              material: updatedProduct.material || '',
              brand: updatedProduct.brand || '',
              size: updatedProduct.size || '',
              color: updatedProduct.color || '',
              status: updatedProduct.status,
              retouch_instructions: updatedProduct.retouch_instructions || '',
            });

            // Show completion notification
            if (updatedProduct.processing_status === 'completed') {
              toast({
                title: 'AI Processing Complete',
                description: 'Images have been processed and content generated',
              });
            } else if (updatedProduct.processing_status === 'failed') {
              toast({
                title: 'AI Processing Failed',
                description: 'There was an error processing the images',
                variant: 'destructive',
              });
            }
          }
        } catch (err) {
          console.error('Failed to poll product status:', err);
        }
      }, 3000);
    }

    // Cleanup on unmount or when product changes
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [product?.id, product?.processing_status, connector, isRetouching, toast]);

  // Handle form change
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Track if instructions have been modified
    if (name === 'retouch_instructions') {
      setInstructionsSaved(value === (product?.retouch_instructions || ''));
    }
  };

  // Handle save
  const handleSave = async () => {
    if (!connector || !product) return;

    // Validate at least one name
    if (!formData.name_en && !formData.name_ar) {
      toast({
        title: 'Validation Error',
        description: 'At least one name (English or Arabic) is required',
        variant: 'destructive',
      });
      return;
    }

    setIsSaving(true);
    try {
      const updated = await uploadConnectorService.updateProduct(
        connector.id,
        product.id,
        formData
      );
      setProduct(updated);
      toast({
        title: 'Saved',
        description: 'Product updated successfully',
      });
    } catch (err) {
      toast({
        title: 'Error',
        description: 'Failed to save product',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Handle save instructions only
  const handleSaveInstructions = async () => {
    if (!connector || !product) return;

    setIsSavingInstructions(true);
    try {
      const updated = await uploadConnectorService.updateProduct(
        connector.id,
        product.id,
        { retouch_instructions: formData.retouch_instructions }
      );
      setProduct(updated);
      setInstructionsSaved(true);
      toast({
        title: 'Instructions Saved',
        description: 'AI retouch instructions have been applied',
      });
    } catch (err) {
      toast({
        title: 'Error',
        description: 'Failed to save instructions',
        variant: 'destructive',
      });
    } finally {
      setIsSavingInstructions(false);
    }
  };

  // Handle delete
  const handleDelete = async () => {
    if (!connector || !product) return;

    setIsDeleting(true);
    try {
      await uploadConnectorService.deleteProduct(connector.id, product.id);
      toast({
        title: 'Deleted',
        description: 'Product deleted successfully',
      });
      navigate('/upload-products');
    } catch (err) {
      toast({
        title: 'Error',
        description: 'Failed to delete product',
        variant: 'destructive',
      });
    } finally {
      setIsDeleting(false);
      setShowDeleteDialog(false);
    }
  };

  // Handle remove image from product
  const handleRemoveImage = async () => {
    if (!connector || !product || !imageToRemove) return;

    setIsRemovingImage(true);
    try {
      await uploadConnectorService.removeImagesFromProduct(
        connector.id,
        product.id,
        [imageToRemove.id]
      );
      toast({
        title: 'Image removed',
        description: 'Image has been removed from the product',
      });
      setImageToRemove(null);
      fetchProduct(); // Refresh
    } catch (err) {
      toast({
        title: 'Error',
        description: 'Failed to remove image',
        variant: 'destructive',
      });
    } finally {
      setIsRemovingImage(false);
    }
  };

  // Handle media approval (Graphic Team)
  const handleMediaApproval = async (approve: boolean) => {
    if (!connector || !product) return;

    setIsApprovingMedia(true);
    try {
      const updated = await uploadConnectorService.approveMedia(
        connector.id,
        product.id,
        approve
      );
      setProduct(updated);
      setFormData((prev) => ({ ...prev, status: updated.status }));
      toast({
        title: approve ? 'Media Approved' : 'Media Approval Revoked',
        description: approve
          ? 'Media has been approved by the Graphic Team'
          : 'Media approval has been revoked',
      });
    } catch (err) {
      toast({
        title: 'Error',
        description: 'Failed to update media approval',
        variant: 'destructive',
      });
    } finally {
      setIsApprovingMedia(false);
    }
  };

  // Handle content approval (Content Team)
  const handleContentApproval = async (approve: boolean) => {
    if (!connector || !product) return;

    setIsApprovingContent(true);
    try {
      const updated = await uploadConnectorService.approveContent(
        connector.id,
        product.id,
        approve
      );
      setProduct(updated);
      setFormData((prev) => ({ ...prev, status: updated.status }));
      toast({
        title: approve ? 'Content Approved' : 'Content Approval Revoked',
        description: approve
          ? 'Content has been approved by the Content Team'
          : 'Content approval has been revoked',
      });
    } catch (err) {
      toast({
        title: 'Error',
        description: 'Failed to update content approval',
        variant: 'destructive',
      });
    } finally {
      setIsApprovingContent(false);
    }
  };

  // Handle push to RetailOS
  const handlePushToRetailOS = async () => {
    if (!connector || !product) return;

    setIsPushingToRetailos(true);
    try {
      const result = await uploadConnectorService.pushToRetailOS(
        connector.id,
        product.id
      );

      // Update product with pushed_at timestamp
      setProduct((prev) => prev ? { ...prev, retailos_pushed_at: result.pushed_at } : prev);

      toast({
        title: 'Pushed to RetailOS',
        description: 'Product has been successfully sent to RetailOS',
      });
    } catch (err: any) {
      toast({
        title: 'Push Failed',
        description: err.message || 'Failed to push product to RetailOS',
        variant: 'destructive',
      });
    } finally {
      setIsPushingToRetailos(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Get all images for the product - define early so handlers can use it
  const getAllImages = (): UploadedImage[] => {
    return product?.uploaded_images || [];
  };
  const allImages = getAllImages();

  // Handle image type toggle — toggles a specific type on/off (reverts to 'product' when toggling off)
  const handleToggleImageType = async (image: UploadedImage, targetType: 'barcode' | 'detail' = 'barcode') => {
    if (!connector) return;

    const currentType = image.image_type || 'product';
    const newType = currentType === targetType ? 'product' : targetType;

    setIsUpdatingImageType(image.id);
    try {
      await uploadConnectorService.updateImageType(connector.id, image.id, newType);
      toast({
        title: 'Image Updated',
        description: newType === 'product' ? 'Image reset to product' : `Image marked as ${newType}`,
      });
      fetchProduct();
    } catch (err) {
      toast({
        title: 'Error',
        description: 'Failed to update image type',
        variant: 'destructive',
      });
    } finally {
      setIsUpdatingImageType(null);
    }
  };

  // Handle revert image to original
  const handleRevertImage = async (image: UploadedImage) => {
    if (!connector) return;

    // Only revert if image has been processed
    if (!image.processed_url) {
      toast({
        title: 'Nothing to Revert',
        description: 'This image has not been processed yet',
      });
      return;
    }

    setIsRevertingImage(image.id);
    try {
      await uploadConnectorService.revertImage(connector.id, image.id);
      toast({
        title: 'Image Reverted',
        description: 'Image restored to original state',
      });
      fetchProduct(); // Refresh to get updated image
    } catch (err) {
      toast({
        title: 'Error',
        description: 'Failed to revert image',
        variant: 'destructive',
      });
    } finally {
      setIsRevertingImage(null);
    }
  };

  // Handle single image Studio Retouch
  // Open single image retouch dialog
  const handleSingleImageRetouch = (image: UploadedImage) => {
    if (!connector) return;

    // Don't allow retouching barcode images
    if (image.image_type === 'barcode') {
      toast({
        title: 'Cannot Retouch Barcode',
        description: 'Studio Retouch cannot be applied to barcode images',
        variant: 'destructive',
      });
      return;
    }

    // Pre-fill with product-level instructions if available
    setSingleRetouchInstructions(product?.retouch_instructions || '');
    setSingleRetouchImage(image);
  };

  // Execute single image retouch with instructions
  const executeSingleImageRetouch = async () => {
    if (!connector || !singleRetouchImage) return;

    const image = singleRetouchImage;
    setSingleRetouchImage(null); // Close dialog

    setIsRetouchingSingleImage(image.id);
    try {
      const result = await uploadConnectorService.studioRetouchSingleImage(
        connector.id,
        image.id,
        singleRetouchInstructions || undefined
      );

      if (result.success) {
        toast({
          title: 'Image Retouched',
          description: `Image processed as ${result.imageType || 'product'} shot`,
        });
        fetchProduct(); // Refresh to get updated image
      } else {
        throw new Error('Retouch failed');
      }
    } catch (err: any) {
      toast({
        title: 'Retouch Failed',
        description: err.message || 'Failed to retouch image',
        variant: 'destructive',
      });
    } finally {
      setIsRetouchingSingleImage(null);
      setSingleRetouchInstructions('');
    }
  };

  // Execute Studio Retouch (called directly or after warning confirmation)
  const executeStudioRetouch = async () => {
    if (!connector || !product) return;

    setIsRetouching(true);
    try {
      const result = await uploadConnectorService.studioRetouch(connector.id, product.id);

      if (result.success) {
        // Build success message with details
        const details: string[] = [];
        if (result.processedImages) {
          details.push(`${result.processedImages} image(s) processed`);
        }
        if (result.barcode) {
          details.push(`Barcode: ${result.barcode}`);
        }
        if (result.title_en) {
          details.push('Title generated');
        }
        if (result.description_en) {
          details.push('Description generated');
        }

        toast({
          title: 'Studio Retouch Complete',
          description: details.length > 0 ? details.join(', ') : 'Processing complete',
        });

        // Show warnings if there were any errors during processing
        if (result.errors && result.errors.length > 0) {
          toast({
            title: 'Some issues occurred',
            description: result.errors.join('; '),
            variant: 'destructive',
          });
        }

        // Refresh product to get updated data from server
        fetchProduct();
      } else {
        throw new Error(result.errors?.join(', ') || 'Studio Retouch failed');
      }
    } catch (err: any) {
      toast({
        title: 'Studio Retouch Failed',
        description: err.message || 'Failed to process images',
        variant: 'destructive',
      });
    } finally {
      setIsRetouching(false);
    }
  };

  // Handle Studio Retouch - checks for barcode and shows warning if missing
  const handleStudioRetouch = async () => {
    if (!connector || !product) return;

    // Check if there are any product images
    const productImages = allImages.filter(
      (img) => img.image_type !== 'barcode'
    );
    if (productImages.length === 0) {
      toast({
        title: 'No Product Images',
        description: 'Please add at least one product image before running Studio Retouch',
        variant: 'destructive',
      });
      return;
    }

    // Check if there's a barcode image marked
    const barcodeImage = allImages.find((img) => img.image_type === 'barcode');

    if (!barcodeImage) {
      // Show warning dialog instead of blocking
      setShowNoBarcodeWarning(true);
      return;
    }

    // Barcode is present, proceed directly
    executeStudioRetouch();
  };

  // Handle confirmation to proceed without barcode
  const handleProceedWithoutBarcode = () => {
    setShowNoBarcodeWarning(false);
    executeStudioRetouch();
  };

  // Get the best display URL for an image (prefer processed > thumbnail > original)
  const getDisplayUrl = (image: UploadedImage): string => {
    return image.processed_url || image.thumbnail_url || image.original_url;
  };

  // Loading skeleton
  if (isLoading) {
    return (
      <div className="space-y-8 animate-pulse">
        {/* Header skeleton */}
        <div className="flex items-center justify-between">
          <div className="h-10 bg-muted rounded w-32"></div>
          <div className="flex gap-3">
            <div className="h-10 bg-muted rounded w-32"></div>
            <div className="h-10 bg-muted rounded w-32"></div>
          </div>
        </div>

        {/* Content skeleton */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Image section skeleton */}
          <div className="space-y-4">
            <div className="aspect-square bg-muted rounded-xl"></div>
            <div className="grid grid-cols-4 gap-2">
              {Array(4)
                .fill(0)
                .map((_, i) => (
                  <div key={i} className="aspect-square bg-muted rounded-lg"></div>
                ))}
            </div>
          </div>

          {/* Info section skeleton */}
          <div className="space-y-6">
            <div className="h-32 bg-muted rounded-xl"></div>
            <div className="h-24 bg-muted rounded-xl"></div>
            <div className="h-20 bg-muted rounded-xl"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="space-y-6">
        <Button variant="outline" onClick={() => navigate('/upload-products')} className="gap-2">
          <ArrowLeft className="h-4 w-4" />
          Back to Products
        </Button>
        <Alert className="bg-muted/50">
          <AlertDescription>Product not found</AlertDescription>
        </Alert>
      </div>
    );
  }

  const hasImages = allImages.length > 0;
  const productName = product.name_en || product.name_ar || 'Untitled Product';

  return (
    <>
      <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <Button
            variant="outline"
            onClick={() => navigate('/upload-products')}
            className="gap-2 mb-3"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Products
          </Button>
          <h1 className="text-3xl font-bold tracking-tight line-clamp-1">{productName}</h1>
          <p className="text-muted-foreground">Product details and image management</p>
        </div>

        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            onClick={() => setShowDeleteDialog(true)}
            className="gap-2 text-destructive hover:text-destructive hover:bg-destructive/10"
          >
            <Trash2 className="h-4 w-4" />
            Delete
          </Button>
          <Button onClick={handleSave} disabled={isSaving} className="gap-2 shadow-md">
            {isSaving ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4" />
                Save Changes
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Actions Card */}
      <Card className={`card-enhanced relative overflow-hidden ${isRetouching ? 'ring-2 ring-purple-500/50' : ''}`}>
        {/* Processing Overlay Bar */}
        {isRetouching && (
          <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-purple-600 to-indigo-600 animate-pulse" />
        )}
        <CardContent className="py-4">
          {isRetouching ? (
            /* Processing State - Replaces all buttons */
            <div className="flex items-center gap-4">
              <div className="relative flex-shrink-0">
                <Loader2 className="h-6 w-6 animate-spin text-purple-500" />
                <div className="absolute inset-0 h-6 w-6 animate-ping opacity-30 rounded-full bg-purple-500" />
              </div>
              <div className="flex-1">
                <p className="text-base font-medium text-purple-200">AI Processing in Progress</p>
                <p className="text-sm text-muted-foreground">Retouching images, extracting barcode, and generating content...</p>
              </div>
              <Wand2 className="h-5 w-5 text-purple-400 animate-pulse flex-shrink-0" />
            </div>
          ) : (
            /* Normal State - Action Buttons */
            <div className="flex items-center justify-between">
              {/* AI Process Button */}
              <div className="flex items-center gap-4">
                <div className="flex flex-col gap-1">
                  <Button
                    variant="default"
                    onClick={handleStudioRetouch}
                    disabled={!hasImages}
                    className="gap-2 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 shadow-lg"
                  >
                    <Wand2 className="h-4 w-4" />
                    AI Process
                  </Button>
                  {/* Credit cost display */}
                  {(() => {
                    const productImageCount = allImages.filter(img => img.image_type !== 'barcode').length;
                    if (!studioRetouchCreditsPerImage || productImageCount === 0) return null;
                    const totalCredits = productImageCount * studioRetouchCreditsPerImage;
                    return (
                      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                        <Coins className="h-3 w-3 text-amber-500" />
                        <span>
                          {totalCredits.toFixed(1)} credits ({productImageCount} {productImageCount === 1 ? 'image' : 'images'})
                        </span>
                      </div>
                    );
                  })()}
                </div>
                <p className="text-sm text-muted-foreground hidden sm:block">
                  Retouch and transform images, extract barcode, and generate content
                </p>
              </div>

              {/* Approval Buttons */}
              <div className="flex items-center gap-3">
                <span className="text-sm text-muted-foreground hidden sm:block">Approval:</span>
                <div className="flex items-center gap-2">
                  <Button
                    variant={product.media_approved ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => handleMediaApproval(!product.media_approved)}
                    disabled={isApprovingMedia}
                    className={product.media_approved ? 'bg-green-600 hover:bg-green-700' : ''}
                  >
                    {isApprovingMedia ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
                    ) : product.media_approved ? (
                      <Check className="h-4 w-4 mr-1.5" />
                    ) : (
                      <Palette className="h-4 w-4 mr-1.5" />
                    )}
                    Media
                  </Button>
                  <Button
                    variant={product.content_approved ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => handleContentApproval(!product.content_approved)}
                    disabled={isApprovingContent}
                    className={product.content_approved ? 'bg-green-600 hover:bg-green-700' : ''}
                  >
                    {isApprovingContent ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
                    ) : product.content_approved ? (
                      <Check className="h-4 w-4 mr-1.5" />
                    ) : (
                      <FileEdit className="h-4 w-4 mr-1.5" />
                    )}
                    Content
                  </Button>
                </div>

                {/* Push to RetailOS - only show when both approvals done */}
                {product.media_approved && product.content_approved && (
                  <div className="flex items-center gap-2 ml-4 pl-4 border-l border-border">
                    {retailosConfigured ? (
                      <Button
                        size="sm"
                        onClick={handlePushToRetailOS}
                        disabled={isPushingToRetailos}
                        className={`gap-2 ${
                          product.retailos_pushed_at
                            ? 'bg-blue-600 hover:bg-blue-700'
                            : 'bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700'
                        }`}
                      >
                        {isPushingToRetailos ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : product.retailos_pushed_at ? (
                          <Check className="h-4 w-4" />
                        ) : (
                          <Send className="h-4 w-4" />
                        )}
                        {isPushingToRetailos
                          ? 'Pushing...'
                          : product.retailos_pushed_at
                            ? 'Pushed'
                            : 'Push to RetailOS'}
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => navigate('/settings')}
                        className="gap-2 text-muted-foreground"
                      >
                        <Settings className="h-4 w-4" />
                        Configure RetailOS
                      </Button>
                    )}
                  </div>
                )}

                {/* Amazon Export - only show when enabled and both approvals done */}
                {amazonEnabled && product.media_approved && product.content_approved && (
                  <div className="flex items-center gap-2 ml-4 pl-4 border-l border-border">
                    {amazonEnrichmentExists ? (
                      <>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => navigate(`/amazon-export?products=${product.id}&view=true`)}
                          className="gap-2 border-amber-500/30 text-amber-600 hover:bg-amber-500/10"
                        >
                          <ShoppingCart className="h-4 w-4" />
                          View Amazon Listing
                        </Button>
                        {amazonLastExported && (
                          <span className="text-xs text-muted-foreground">
                            Last exported {new Date(amazonLastExported).toLocaleDateString()}
                          </span>
                        )}
                      </>
                    ) : (
                      <Button
                        size="sm"
                        onClick={() => navigate(`/amazon-export?products=${product.id}`)}
                        className="gap-2 bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700"
                      >
                        <ShoppingCart className="h-4 w-4" />
                        Export for Amazon
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Images Section */}
        <div className="space-y-6">
          <Card className="card-enhanced">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-2">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <ImageIcon className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <div className="text-lg font-semibold">Product Images</div>
                  <div className="text-sm text-muted-foreground">
                    {allImages.length} image{allImages.length !== 1 ? 's' : ''} available
                  </div>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {hasImages ? (
                <div className="space-y-6">
                  {/* Main image display */}
                  {(() => {
                    const currentImage = allImages.find(
                      (img) => getDisplayUrl(img) === selectedImage
                    ) || allImages[0];
                    const isCurrentBarcode = currentImage?.image_type === 'barcode';
                    const isCurrentProcessed = currentImage?.status === 'completed' && currentImage?.processed_url;
                    const isCurrentUpdating = isUpdatingImageType === currentImage?.id;
                    const isCurrentReverting = isRevertingImage === currentImage?.id;
                    const isCurrentRetouching = isRetouchingSingleImage === currentImage?.id;
                    const canCompare = isCurrentProcessed && currentImage?.original_url && currentImage?.processed_url;

                    return (
                      <div className="relative group">
                        {/* Image container */}
                        <div
                          className="aspect-square bg-gradient-to-br from-muted/50 to-muted rounded-xl overflow-hidden shadow-lg cursor-pointer relative"
                          onClick={() => {
                            if (currentImage) {
                              setFullscreenImage(currentImage);
                            }
                          }}
                        >
                          <img
                            src={selectedImage || getDisplayUrl(allImages[0])}
                            alt={productName}
                            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                          />
                        </div>

                        {/* Image indicator */}
                        <div className="absolute top-4 right-4 bg-black/80 backdrop-blur-sm text-white text-sm px-3 py-1.5 rounded-full">
                          {allImages.findIndex(
                            (img) => getDisplayUrl(img) === selectedImage
                          ) + 1 || 1}{' '}
                          of {allImages.length}
                        </div>

                        {/* Type badge for main image */}
                        {isCurrentBarcode && (
                          <div className="absolute top-4 left-4 bg-amber-500 text-white text-sm px-3 py-1.5 rounded-full font-medium flex items-center gap-1.5">
                            <ScanBarcode className="h-4 w-4" />
                            Barcode Image
                          </div>
                        )}
                        {currentImage?.image_type === 'detail' && (
                          <div className="absolute top-4 left-4 bg-blue-500 text-white text-sm px-3 py-1.5 rounded-full font-medium flex items-center gap-1.5">
                            <Search className="h-4 w-4" />
                            Detail Shot
                          </div>
                        )}

                        {/* Action buttons for main image */}
                        {currentImage && (
                          <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between opacity-0 group-hover:opacity-100 transition-opacity">
                            {/* Left side - Status indicators and actions */}
                            <div className="flex items-center gap-2">
                              {/* Compare button - only for processed images */}
                              {canCompare && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setComparingImage(currentImage);
                                  }}
                                  className="px-3 py-1.5 rounded-full text-sm font-medium flex items-center gap-1.5 shadow-lg transition-colors bg-black/70 hover:bg-blue-500 text-white"
                                >
                                  <SplitSquareHorizontal className="h-4 w-4" />
                                  Compare
                                </button>
                              )}

                              {/* Processed indicator */}
                              {isCurrentProcessed && !isCurrentBarcode && (
                                <div className="bg-green-500 text-white px-3 py-1.5 rounded-full text-sm font-medium flex items-center gap-1.5 shadow-lg">
                                  <Check className="h-4 w-4" />
                                  Processed
                                </div>
                              )}

                              {/* Revert button */}
                              {isCurrentProcessed && !isCurrentBarcode && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleRevertImage(currentImage);
                                  }}
                                  disabled={isCurrentReverting}
                                  className="bg-orange-500 hover:bg-orange-600 text-white px-3 py-1.5 rounded-full text-sm font-medium flex items-center gap-1.5 shadow-lg transition-colors"
                                >
                                  {isCurrentReverting ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                  ) : (
                                    <Undo2 className="h-4 w-4" />
                                  )}
                                  Revert
                                </button>
                              )}

                              {/* Single image retouch button */}
                              {!isCurrentBarcode && !currentImage.processed_url && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleSingleImageRetouch(currentImage);
                                  }}
                                  disabled={isCurrentRetouching}
                                  className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white px-3 py-1.5 rounded-full text-sm font-medium flex items-center gap-1.5 shadow-lg transition-colors"
                                  title={studioRetouchCreditsPerImage ? `Retouch this image (${studioRetouchCreditsPerImage} credits)` : 'Retouch this image'}
                                >
                                  {isCurrentRetouching ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                  ) : (
                                    <Wand2 className="h-4 w-4" />
                                  )}
                                  Retouch
                                  {studioRetouchCreditsPerImage && (
                                    <span className="text-xs opacity-80">({studioRetouchCreditsPerImage})</span>
                                  )}
                                </button>
                              )}
                            </div>

                            {/* Right side - Image type toggles */}
                            <div className="flex items-center gap-2">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleToggleImageType(currentImage, 'detail');
                                }}
                                disabled={isCurrentUpdating}
                                className={`px-3 py-1.5 rounded-full text-sm font-medium flex items-center gap-1.5 shadow-lg transition-colors ${
                                  currentImage?.image_type === 'detail'
                                    ? 'bg-blue-500 hover:bg-blue-600 text-white'
                                    : 'bg-black/70 hover:bg-blue-500 text-white'
                                }`}
                              >
                                {isCurrentUpdating ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <Search className="h-4 w-4" />
                                )}
                                {currentImage?.image_type === 'detail' ? 'Unmark Detail' : 'Detail Shot'}
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleToggleImageType(currentImage, 'barcode');
                                }}
                                disabled={isCurrentUpdating}
                                className={`px-3 py-1.5 rounded-full text-sm font-medium flex items-center gap-1.5 shadow-lg transition-colors ${
                                  isCurrentBarcode
                                    ? 'bg-amber-500 hover:bg-amber-600 text-white'
                                    : 'bg-black/70 hover:bg-amber-500 text-white'
                                }`}
                              >
                                {isCurrentUpdating ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <ScanBarcode className="h-4 w-4" />
                                )}
                                {isCurrentBarcode ? 'Unmark Barcode' : 'Barcode'}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {/* Thumbnail gallery */}
                  {allImages.length > 0 && (
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <h4 className="text-sm font-medium text-muted-foreground">Gallery</h4>
                        <p className="text-xs text-muted-foreground">Mark images: barcode or detail shot</p>
                      </div>
                      <div className="grid grid-cols-4 gap-3">
                        {allImages.map((image, index) => {
                          const imgUrl = getDisplayUrl(image);
                          const isSelected = selectedImage === imgUrl;
                          const isBarcode = image.image_type === 'barcode';
                          const isDetail = image.image_type === 'detail';
                          const isUpdating = isUpdatingImageType === image.id;

                          return (
                            <div
                              key={image.id}
                              className={`relative group aspect-square bg-muted rounded-lg overflow-hidden border-2 transition-all duration-200 hover:shadow-md ${
                                isBarcode
                                  ? 'border-amber-500 ring-2 ring-amber-500/20'
                                  : isDetail
                                  ? 'border-blue-500 ring-2 ring-blue-500/20'
                                  : isSelected
                                  ? 'border-primary ring-2 ring-primary/20'
                                  : 'border-transparent hover:border-primary/30'
                              }`}
                            >
                              <button
                                onClick={() => setSelectedImage(imgUrl)}
                                className="w-full h-full"
                              >
                                <img
                                  src={imgUrl}
                                  alt={`${productName} - ${index + 1}`}
                                  className="w-full h-full object-cover transition-transform duration-200 group-hover:scale-110"
                                />
                              </button>

                              {/* Barcode toggle button */}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleToggleImageType(image, 'barcode');
                                }}
                                disabled={isUpdating}
                                className={`absolute top-1 left-1 p-1.5 rounded-full transition-all ${
                                  isBarcode
                                    ? 'bg-amber-500 text-white'
                                    : 'bg-black/70 text-white opacity-0 group-hover:opacity-100 hover:bg-amber-500'
                                }`}
                                title={isBarcode ? 'Marked as barcode (click to unmark)' : 'Mark as barcode'}
                              >
                                {isUpdating ? (
                                  <Loader2 className="h-3 w-3 animate-spin" />
                                ) : (
                                  <ScanBarcode className="h-3 w-3" />
                                )}
                              </button>

                              {/* Detail shot toggle button */}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleToggleImageType(image, 'detail');
                                }}
                                disabled={isUpdating}
                                className={`absolute top-1 left-8 p-1.5 rounded-full transition-all ${
                                  isDetail
                                    ? 'bg-blue-500 text-white'
                                    : 'bg-black/70 text-white opacity-0 group-hover:opacity-100 hover:bg-blue-500'
                                }`}
                                title={isDetail ? 'Marked as detail shot (click to unmark)' : 'Mark as detail shot'}
                              >
                                {isUpdating ? (
                                  <Loader2 className="h-3 w-3 animate-spin" />
                                ) : (
                                  <Search className="h-3 w-3" />
                                )}
                              </button>

                              {/* Remove button */}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setImageToRemove(image);
                                }}
                                className="absolute top-1 right-1 p-1.5 rounded-full bg-black/70 text-white opacity-0 group-hover:opacity-100 transition-opacity hover:bg-destructive"
                              >
                                <X className="h-3 w-3" />
                              </button>

                              {/* Processed indicator with revert button */}
                              {image.status === 'completed' && image.processed_url && !isBarcode && (
                                <div className="absolute bottom-1 left-1 flex items-center gap-1">
                                  <div className="bg-green-500 text-white p-1 rounded-full shadow-lg">
                                    <Check className="h-3 w-3" />
                                  </div>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleRevertImage(image);
                                    }}
                                    disabled={isRevertingImage === image.id}
                                    className="bg-orange-500 text-white p-1 rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-opacity hover:bg-orange-600"
                                    title="Revert to original"
                                  >
                                    {isRevertingImage === image.id ? (
                                      <Loader2 className="h-3 w-3 animate-spin" />
                                    ) : (
                                      <Undo2 className="h-3 w-3" />
                                    )}
                                  </button>
                                </div>
                              )}

                              {/* Single image retouch button - shows for unprocessed product/detail images */}
                              {!isBarcode && !image.processed_url && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleSingleImageRetouch(image);
                                  }}
                                  disabled={isRetouchingSingleImage === image.id}
                                  className="absolute bottom-1 right-1 p-1.5 rounded-full bg-gradient-to-r from-purple-600 to-indigo-600 text-white shadow-lg opacity-0 group-hover:opacity-100 transition-opacity hover:from-purple-700 hover:to-indigo-700"
                                  title={studioRetouchCreditsPerImage ? `Apply Studio Retouch (${studioRetouchCreditsPerImage} credits)` : 'Apply Studio Retouch'}
                                >
                                  {isRetouchingSingleImage === image.id ? (
                                    <Loader2 className="h-3 w-3 animate-spin" />
                                  ) : (
                                    <Wand2 className="h-3 w-3" />
                                  )}
                                </button>
                              )}

                              {/* Barcode label */}
                              {isBarcode && (
                                <div className="absolute bottom-0 left-0 right-0 bg-amber-500/90 text-white text-xs py-0.5 text-center font-medium">
                                  Barcode
                                </div>
                              )}

                              {isSelected && !isBarcode && (
                                <div className="absolute inset-0 bg-primary/20 flex items-center justify-center pointer-events-none">
                                  <div className="w-3 h-3 bg-primary rounded-full"></div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="aspect-square bg-gradient-to-br from-muted/50 to-muted rounded-xl flex items-center justify-center">
                  <div className="text-center">
                    <div className="p-4 bg-muted/50 rounded-full mb-4 inline-block">
                      <FileImage className="h-16 w-16 text-muted-foreground" />
                    </div>
                    <p className="text-muted-foreground font-medium">No images available</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      Add images from the Assets page
                    </p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* AI Retouch Instructions */}
          <Card className="card-enhanced border-orange-200 dark:border-orange-800">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-3">
                <div className="p-2 bg-orange-100 dark:bg-orange-900/30 rounded-lg">
                  <Wand2 className="h-5 w-5 text-orange-600 dark:text-orange-400" />
                </div>
                <div>
                  <div className="text-lg font-semibold">AI Retouch Instructions</div>
                  <div className="text-sm text-muted-foreground">Guide the AI on how to process images</div>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <Label htmlFor="retouch_instructions" className="text-sm font-medium text-muted-foreground flex items-center gap-1">
                  <div className="w-1.5 h-1.5 bg-orange-500 rounded-full"></div>
                  Special Instructions (Optional)
                </Label>
                <Textarea
                  id="retouch_instructions"
                  name="retouch_instructions"
                  value={formData.retouch_instructions}
                  onChange={handleChange}
                  placeholder="E.g., 'Keep the frame intact - this is a framed artwork', 'Preserve the background elements', 'Product includes the stand'"
                  rows={3}
                  className="bg-muted/50"
                />
                <div className="flex items-center justify-between">
                  <p className="text-xs text-muted-foreground">
                    {formData.retouch_instructions
                      ? 'Click "Apply" to save, then use "AI Process" above.'
                      : product?.retouch_instructions
                        ? 'Click "Clear" to remove saved instructions.'
                        : 'Add instructions to guide the AI retouch process.'}
                  </p>
                  {(() => {
                    const hasCurrentText = !!formData.retouch_instructions;
                    const hasSavedText = !!product?.retouch_instructions;
                    const isClearing = !hasCurrentText && hasSavedText && !instructionsSaved;

                    return (
                      <Button
                        onClick={handleSaveInstructions}
                        disabled={isSavingInstructions || instructionsSaved}
                        size="sm"
                        variant={!hasCurrentText && !isClearing ? 'outline' : 'default'}
                        className={`gap-2 ${
                          isClearing
                            ? 'bg-red-600 hover:bg-red-700'
                            : !hasCurrentText
                              ? ''
                              : instructionsSaved
                                ? 'bg-green-600 hover:bg-green-600'
                                : 'bg-orange-600 hover:bg-orange-700'
                        }`}
                      >
                        {isSavingInstructions ? (
                          <>
                            <Loader2 className="h-3 w-3 animate-spin" />
                            Saving...
                          </>
                        ) : isClearing ? (
                          <>
                            <Trash2 className="h-3 w-3" />
                            Clear
                          </>
                        ) : !hasCurrentText ? (
                          <>
                            <Wand2 className="h-3 w-3" />
                            No Instructions
                          </>
                        ) : instructionsSaved ? (
                          <>
                            <Check className="h-3 w-3" />
                            Applied
                          </>
                        ) : (
                          <>
                            <Save className="h-3 w-3" />
                            Apply
                          </>
                        )}
                      </Button>
                    );
                  })()}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Product Information */}
        <div className="space-y-6">
          {/* Basic Info */}
          <Card className="card-enhanced">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-primary/10 rounded-lg">
                    <Package className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <div className="text-lg font-semibold">Product Information</div>
                    <div className="text-sm text-muted-foreground">Basic product details</div>
                  </div>
                </div>
                <Badge
                  variant={product.status === 'complete' ? 'default' : product.status === 'partial' ? 'outline' : 'secondary'}
                  className={
                    product.status === 'complete'
                      ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white'
                      : product.status === 'partial'
                      ? 'border-amber-500 text-amber-500'
                      : ''
                  }
                >
                  {product.status === 'complete' && <CheckCircle2 className="h-3 w-3 mr-1" />}
                  {product.status === 'partial' && <AlertCircle className="h-3 w-3 mr-1" />}
                  {product.status === 'draft' && <Clock className="h-3 w-3 mr-1" />}
                  {product.status === 'complete' ? 'Complete' : product.status === 'partial' ? 'Partial' : 'Draft'}
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Name EN */}
              <div className="space-y-2">
                <Label htmlFor="name_en" className="text-sm font-medium text-muted-foreground flex items-center gap-1">
                  <div className="w-1.5 h-1.5 bg-primary rounded-full"></div>
                  Name (English)
                </Label>
                <Input
                  id="name_en"
                  name="name_en"
                  value={formData.name_en}
                  onChange={handleChange}
                  placeholder="Enter product name in English"
                  className="bg-muted/50"
                />
              </div>

              {/* Name AR */}
              <div className="space-y-2">
                <Label htmlFor="name_ar" className="text-sm font-medium text-muted-foreground flex items-center gap-1">
                  <div className="w-1.5 h-1.5 bg-primary rounded-full"></div>
                  Name (Arabic)
                </Label>
                <Input
                  id="name_ar"
                  name="name_ar"
                  value={formData.name_ar}
                  onChange={handleChange}
                  placeholder="Enter product name in Arabic"
                  dir="rtl"
                  className="bg-muted/50"
                />
              </div>

              {/* Barcode */}
              <div className="space-y-2">
                <Label htmlFor="barcode" className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                  <Barcode className="h-4 w-4" />
                  Barcode
                </Label>
                <Input
                  id="barcode"
                  name="barcode"
                  value={formData.barcode}
                  onChange={handleChange}
                  placeholder="Enter product barcode"
                  className="bg-muted/50 font-mono"
                />
                {amazonEnabled && (
                  <p className="text-xs text-amber-500">Very important if provided — improves Amazon listing accuracy</p>
                )}
              </div>

              {/* SKU */}
              <div className="space-y-2">
                <Label htmlFor="sku" className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                  <Package className="h-4 w-4" />
                  SKU
                </Label>
                <Input
                  id="sku"
                  name="sku"
                  value={formData.sku}
                  onChange={handleChange}
                  placeholder="Enter product SKU"
                  className="bg-muted/50 font-mono"
                />
                <p className="text-xs text-muted-foreground">Optional — auto-generated if not provided</p>
              </div>

              {/* Price */}
              <div className="space-y-2">
                <Label htmlFor="price" className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                  <DollarSign className="h-4 w-4" />
                  Price <span className="text-red-400 text-xs">*</span>
                </Label>
                <Input
                  id="price"
                  name="price"
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.price}
                  onChange={handleChange}
                  placeholder="0.00"
                  className="bg-muted/50"
                />
              </div>

              {/* Quantity */}
              <div className="space-y-2">
                <Label htmlFor="quantity" className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                  <ShoppingCart className="h-4 w-4" />
                  Quantity <span className="text-red-400 text-xs">*</span>
                </Label>
                <Input
                  id="quantity"
                  name="quantity"
                  type="number"
                  step="1"
                  min="0"
                  value={formData.quantity}
                  onChange={handleChange}
                  placeholder="Enter quantity"
                  className="bg-muted/50"
                />
              </div>

              {/* Brand */}
              <div className="space-y-2">
                <Label htmlFor="brand" className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                  <Tag className="h-4 w-4" />
                  Brand
                </Label>
                <Input
                  id="brand"
                  name="brand"
                  value={formData.brand}
                  onChange={handleChange}
                  placeholder="Enter brand name"
                  className="bg-muted/50"
                />
                <p className="text-xs text-muted-foreground">Enhances quality of AI detection and generation</p>
              </div>

              {/* Material */}
              <div className="space-y-2">
                <Label htmlFor="material" className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Material
                </Label>
                <Input
                  id="material"
                  name="material"
                  value={formData.material}
                  onChange={handleChange}
                  placeholder="e.g. 100% Cotton"
                  className="bg-muted/50"
                />
                {amazonEnabled && (
                  <p className="text-xs text-amber-500">Very important — key attribute for Amazon listings</p>
                )}
              </div>

              {/* Size */}
              <div className="space-y-2">
                <Label htmlFor="size" className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                  <Tag className="h-4 w-4" />
                  Size
                </Label>
                <Input
                  id="size"
                  name="size"
                  value={formData.size}
                  onChange={handleChange}
                  placeholder="e.g. M, L, XL, 42"
                  className="bg-muted/50"
                />
                {amazonEnabled && (
                  <p className="text-xs text-amber-500">Very important — required for most Amazon categories</p>
                )}
              </div>

              {/* Color */}
              <div className="space-y-2">
                <Label htmlFor="color" className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                  <Palette className="h-4 w-4" />
                  Color
                </Label>
                <Input
                  id="color"
                  name="color"
                  value={formData.color}
                  onChange={handleChange}
                  placeholder="e.g. Navy Blue, Red/White"
                  className="bg-muted/50"
                />
                <p className="text-xs text-muted-foreground">Good to have — AI can also detect from images</p>
              </div>

            </CardContent>
          </Card>

          {/* Descriptions */}
          <Card className="card-enhanced">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-3">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <FileText className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <div className="text-lg font-semibold">Descriptions</div>
                  <div className="text-sm text-muted-foreground">Product descriptions</div>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Description EN */}
              <div className="space-y-2">
                <Label htmlFor="description_en" className="text-sm font-medium text-muted-foreground flex items-center gap-1">
                  <div className="w-1.5 h-1.5 bg-primary rounded-full"></div>
                  Description (English)
                </Label>
                <Textarea
                  id="description_en"
                  name="description_en"
                  value={formData.description_en}
                  onChange={handleChange}
                  placeholder="Enter product description in English"
                  rows={4}
                  className="bg-muted/50"
                />
              </div>

              {/* Description AR */}
              <div className="space-y-2">
                <Label htmlFor="description_ar" className="text-sm font-medium text-muted-foreground flex items-center gap-1">
                  <div className="w-1.5 h-1.5 bg-primary rounded-full"></div>
                  Description (Arabic)
                </Label>
                <Textarea
                  id="description_ar"
                  name="description_ar"
                  value={formData.description_ar}
                  onChange={handleChange}
                  placeholder="Enter product description in Arabic"
                  dir="rtl"
                  rows={4}
                  className="bg-muted/50"
                />
              </div>
            </CardContent>
          </Card>

          {/* Timeline */}
          <Card className="card-enhanced">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-3">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Calendar className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <div className="text-lg font-semibold">Timeline</div>
                  <div className="text-sm text-muted-foreground">Product lifecycle dates</div>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-muted-foreground flex items-center gap-1">
                  <div className="w-1.5 h-1.5 bg-primary rounded-full"></div>
                  Created
                </label>
                <p className="text-sm font-mono bg-muted/50 px-3 py-2 rounded-lg border">
                  {formatDate(product.created_at)}
                </p>
              </div>

              {product.updated_at && product.updated_at !== product.created_at && (
                <div className="space-y-2">
                  <label className="text-sm font-medium text-muted-foreground flex items-center gap-1">
                    <div className="w-1.5 h-1.5 bg-success rounded-full"></div>
                    Last Updated
                  </label>
                  <p className="text-sm font-mono bg-muted/50 px-3 py-2 rounded-lg border">
                    {formatDate(product.updated_at)}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
      </div>

      {/* Delete Product Dialog */}
      <ConfirmDialog
        open={showDeleteDialog}
        onOpenChange={setShowDeleteDialog}
        title="Delete Product"
        description={`Are you sure you want to delete "${productName}"? The images will be unlinked but not deleted.`}
        confirmText="Delete"
        loadingText="Deleting..."
        onConfirm={handleDelete}
        variant="destructive"
        isLoading={isDeleting}
      />

      {/* Remove Image Dialog */}
      <ConfirmDialog
        open={!!imageToRemove}
        onOpenChange={(open) => !open && setImageToRemove(null)}
        title="Remove Image"
        description="Are you sure you want to remove this image from the product? The image will not be deleted, just unlinked."
        confirmText="Remove"
        loadingText="Removing..."
        onConfirm={handleRemoveImage}
        variant="destructive"
        isLoading={isRemovingImage}
      />

      {/* No Barcode Warning Dialog */}
      <ConfirmDialog
        open={showNoBarcodeWarning}
        onOpenChange={setShowNoBarcodeWarning}
        title="No Barcode Image Selected"
        description={(() => {
          const productImageCount = allImages.filter(img => img.image_type !== 'barcode').length;
          const totalCredits = studioRetouchCreditsPerImage ? productImageCount * studioRetouchCreditsPerImage : 0;
          return `You haven't marked any image as a barcode. The AI will process ${productImageCount} image${productImageCount !== 1 ? 's' : ''} without extracting barcode information.${totalCredits > 0 ? ` This will cost ${totalCredits.toFixed(1)} credits.` : ''} Are you sure you want to continue?`;
        })()}
        confirmText="Continue Without Barcode"
        cancelText="Cancel"
        onConfirm={handleProceedWithoutBarcode}
        variant="default"
      />

      {/* Single Image Retouch Dialog */}
      <Dialog open={!!singleRetouchImage} onOpenChange={(open) => !open && setSingleRetouchImage(null)}>
        <DialogContent className="sm:max-w-md overflow-hidden">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wand2 className="h-5 w-5 text-purple-600" />
              Retouch Image
            </DialogTitle>
            <DialogDescription>
              Add optional instructions to guide the AI retouch process for this image.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {singleRetouchImage && (
              <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg overflow-hidden">
                <img
                  src={singleRetouchImage.thumbnail_url || singleRetouchImage.original_url}
                  alt={singleRetouchImage.original_filename}
                  className="w-16 h-16 object-cover rounded"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate max-w-[280px]">{singleRetouchImage.original_filename}</p>
                  <p className="text-xs text-muted-foreground">
                    {studioRetouchCreditsPerImage ? `${studioRetouchCreditsPerImage} credits` : 'Processing...'}
                  </p>
                </div>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="single_retouch_instructions" className="text-sm font-medium">
                Instructions (Optional)
              </Label>
              <Textarea
                id="single_retouch_instructions"
                value={singleRetouchInstructions}
                onChange={(e) => setSingleRetouchInstructions(e.target.value)}
                placeholder="E.g., 'Keep the frame intact', 'Preserve the background', 'This includes a stand'"
                rows={3}
                className="bg-muted/50"
              />
              <p className="text-xs text-muted-foreground">
                Tell the AI what to preserve or handle specially for this image.
              </p>
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setSingleRetouchImage(null)}
            >
              Cancel
            </Button>
            <Button
              onClick={executeSingleImageRetouch}
              className="gap-2 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700"
            >
              <Wand2 className="h-4 w-4" />
              Retouch
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Fullscreen Image Viewer */}
      <ImageViewerModal
        isOpen={!!fullscreenImage}
        imageUrl={fullscreenImage?.processed_url || fullscreenImage?.original_url || ''}
        filename={fullscreenImage?.original_filename || productName}
        onClose={() => setFullscreenImage(null)}
        originalUrl={fullscreenImage?.original_url}
        onCompare={fullscreenImage?.processed_url ? () => {
          // Switch from viewer to compare modal
          setComparingImage(fullscreenImage);
          setFullscreenImage(null);
        } : undefined}
      />

      {/* Compare Modal */}
      {comparingImage && comparingImage.processed_url && (
        <ImageCompareModal
          isOpen={!!comparingImage}
          originalUrl={comparingImage.original_url}
          processedUrl={comparingImage.processed_url}
          filename={comparingImage.original_filename}
          onClose={() => setComparingImage(null)}
          onRevert={() => {
            handleRevertImage(comparingImage);
            setComparingImage(null);
          }}
        />
      )}
    </>
  );
};
